import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { useI18n } from '@/lib/i18n';
import { PUBLIC_WEB_URL, isNativeApp } from '@/lib/capacitor';
import { Link2, Copy, MessageCircle, Send, Check, Share2, Info } from 'lucide-react';
import { toast } from 'sonner';

// Shows a small button that, when tapped, opens a dialog with the private
// admin access link for the current business plus quick-share actions.
// Requires `publicCode` (BV-XXXXX) of the business and its `businessName`.
export function ShareAdminLinkButton({ publicCode, businessName, className = '' }) {
  const { language } = useI18n();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const es = language === 'es';

  if (!publicCode) return null;

  const link = `${PUBLIC_WEB_URL}/staff/${publicCode}`;
  const message = es
    ? `Hola, aquí tienes el acceso para administrar la agenda de "${businessName}":\n\n${link}\n\nUsa tu PIN personal para entrar. Guárdalo en tus favoritos del navegador para acceso rápido.`
    : `Hi, here's your access link to manage "${businessName}"'s schedule:\n\n${link}\n\nUse your personal PIN to sign in. Bookmark it for quick access.`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(es ? 'Link copiado' : 'Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text
      toast.error(es ? 'Copia el link manualmente' : 'Copy the link manually');
    }
  };

  const handleShareNative = async () => {
    // Use Web Share API on capable devices (mobile browsers + native app via Capacitor's fallback)
    if (navigator.share) {
      try {
        await navigator.share({
          title: es ? 'Acceso de Administrador · Bookvia' : 'Administrator Access · Bookvia',
          text: message,
          url: link,
        });
      } catch {
        // user cancelled — no-op
      }
    } else {
      handleCopy();
    }
  };

  const handleWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  const handleSMS = () => {
    // sms: URI works on iOS + most Android
    const sms = `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = sms;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5 border-amber-200 text-amber-800 hover:bg-amber-50 dark:border-amber-800/40 dark:text-amber-200 ${className}`}
          data-testid="share-admin-link-btn"
        >
          <Link2 className="h-3.5 w-3.5" />
          {es ? 'Link para administradores' : 'Admin access link'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="share-admin-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-amber-600" />
            {es ? 'Link de acceso para administradores' : 'Administrator access link'}
          </DialogTitle>
          <DialogDescription>
            {es
              ? 'Comparte este link privado con tus administradores. Al abrirlo, verán la lista de admins y solo tendrán que meter su PIN.'
              : 'Share this private link with your administrators. Opening it shows the admin list — they only need their PIN.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Link display */}
          <div className="p-3 rounded-lg bg-muted/60 border border-border/60 break-all font-mono text-xs" data-testid="admin-link-value">
            {link}
          </div>

          {/* Copy button */}
          <Button
            type="button"
            className="w-full h-11 btn-coral"
            onClick={handleCopy}
            data-testid="copy-admin-link-btn"
          >
            {copied ? (
              <><Check className="h-4 w-4 mr-2" />{es ? '¡Copiado!' : 'Copied!'}</>
            ) : (
              <><Copy className="h-4 w-4 mr-2" />{es ? 'Copiar link' : 'Copy link'}</>
            )}
          </Button>

          {/* Quick share options */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 flex-col gap-0.5 text-xs"
              onClick={handleWhatsApp}
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4 text-emerald-600" />
              WhatsApp
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 flex-col gap-0.5 text-xs"
              onClick={handleSMS}
              data-testid="share-sms-btn"
            >
              <Send className="h-4 w-4 text-blue-600" />
              SMS
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 flex-col gap-0.5 text-xs"
              onClick={handleShareNative}
              disabled={!navigator.share && !isNativeApp()}
              data-testid="share-native-btn"
            >
              <Share2 className="h-4 w-4 text-purple-600" />
              {es ? 'Más' : 'More'}
            </Button>
          </div>

          {/* Tips */}
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/70 p-3 flex gap-2">
            <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed space-y-1">
              <p>
                {es
                  ? 'Este link es privado y único de tu negocio. Solo tus administradores designados con PIN pueden usarlo para entrar.'
                  : 'This link is private and unique to your business. Only your designated administrators with a PIN can use it.'}
              </p>
              <p>
                {es
                  ? 'Sugerencia: dile a tu administrador que lo guarde en la pantalla de inicio de su celular como un acceso directo.'
                  : "Tip: ask your administrator to save it to their phone's home screen as a shortcut."}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
